// Plays songs in the page and knows when every beat will be heard. Beats come
// from the saved analysis (worked out ahead of time), not from listening.

import type { SavedAnalysis } from "../shared/library.ts";
import { audioTimeToEpochMs } from "./audio-clock.ts";

export interface Track {
  id: string;
  title: string;
  analysis: SavedAnalysis;
}

interface Playing {
  track: Track;
  source: AudioBufferSourceNode;
  // Wall-clock ms when the song's first sample is heard.
  startEpochMs: number;
}

// Start playback this far ahead so the first beats reach every house in time.
const PREROLL_SECONDS = 0.8;

export class SongPlayer {
  current: Playing | null = null;
  private ctx: AudioContext | null = null;
  private readonly onEnded: (track: Track) => void;

  constructor(onEnded: (track: Track) => void) {
    this.onEnded = onEnded;
  }

  audio(): AudioContext {
    this.ctx ??= new AudioContext();
    return this.ctx;
  }

  decode(data: ArrayBuffer): Promise<AudioBuffer> {
    return this.audio().decodeAudioData(data);
  }

  play(track: Track, buffer: AudioBuffer): void {
    this.stop();
    const ctx = this.audio();
    void ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const when = ctx.currentTime + PREROLL_SECONDS;
    source.start(when);
    const playing: Playing = { track, source, startEpochMs: audioTimeToEpochMs(ctx, when) };
    this.current = playing;
    source.onended = () => {
      if (this.current !== playing) return;
      this.current = null;
      this.onEnded(track);
    };
  }

  stop(): void {
    const playing = this.current;
    if (!playing) return;
    this.current = null;
    playing.source.onended = null;
    playing.source.stop();
  }

  // Seconds into the current song (negative during the preroll).
  position(): number {
    return this.current ? (Date.now() - this.current.startEpochMs) / 1000 : 0;
  }

  // Wall-clock ms of the current song's beats within [fromMs, toMs).
  beatsBetween(fromMs: number, toMs: number): number[] {
    const playing = this.current;
    if (!playing) return [];
    const out: number[] = [];
    for (const beat of playing.track.analysis.beats) {
      const at = playing.startEpochMs + beat * 1000;
      if (at >= fromMs && at < toMs) out.push(at);
    }
    return out;
  }
}

export function mixdown(buffer: AudioBuffer): Float32Array {
  const out = new Float32Array(buffer.length);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < out.length; i++) out[i] = out[i]! + data[i]! / buffer.numberOfChannels;
  }
  return out;
}
